import { AbacoWebModule, RouterReadyEvent, VueRouter, ApplicationReadyEvent, MenuItem } from '@abaco/web-common/src/app';
import myRoutes from "./routes";
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import { createMenu } from './menu';
import SsoWebModule, { MODULE_ID as SSO_MODULE_ID } from "@abaco/sso-web-module/src/abaco-module";
import StationApi from './api/StationApi';
import SensorApi from './api/SensorApi';
import OAuthApi from './api/OAuthApi';
import SensorDataApi from './api/SensorDataApi';
import NearbyPlotApi from './api/NearbyPlotApi';

import FarmerRegistryModule from '@abaco/farm-registry/src/modules/farm-registry';
import MapModule from '@abaco/map-web-module/src/modules/map-web-module';
import GlobalStateModule from '@abaco/portal-global-state/src/modules/global-state';
import IotVuexModule from './state';
import AccountsApi from './api/AccountsApi';
import DashboardChartsModule from "@abaco/dashboard-module/src/modules/charts/index";
import SensorConfigApi from './api/SensorConfigApi';
import IotIcons from './components/IotIcons';
import AgrirouterApi from './api/AgrirouterApi';
import { SensorWidgetMap, SensorWidgetParam } from './models/SensorModel';
import { createGlobals, deleteGlobals, normalGlobals } from "./globals";
import DashboardModule from '@abaco/dashboard-module/src/modules/dashboard';


export const MODULE_ID = "iot_module";

const state = new IotVuexModule();

export default class IotModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;
    public widgetParams: SensorWidgetMap = {};

    private _iotIcons!: IotIcons;

    private apiCreated = false;

    private ssoModule!: SsoWebModule;
    private dashboardModule!: DashboardModule;
    private stnApi!: StationApi;
    private snsrApi!: SensorApi;
    private npApi!: NearbyPlotApi;
    private sdApi!: SensorDataApi;
    private accntsApi!: AccountsApi;
    private scApi!: SensorConfigApi;
    private AuthApi!: OAuthApi;
    private agriRoutApi!: AgrirouterApi;
    private router!: VueRouter;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly globalComponents = normalGlobals;
    readonly vuexModule = state;
    menu: MenuItem[] = [];

    private alreadyAdded = false;

    dependsOn = {
        [FarmerRegistryModule.MODULE_ID]: () => new FarmerRegistryModule(),
        [MapModule.MODULE_ID]: () => new MapModule(),
        [GlobalStateModule.MODULE_ID]: () => new GlobalStateModule(),
        [DashboardChartsModule.MODULE_ID]: () => new DashboardChartsModule(),
    }

    onRouterReady(evt: RouterReadyEvent) {
        this.router = evt.router;
        this.menu = createMenu(this.router);
    }

    onApplicationReady(evt: ApplicationReadyEvent) {
        const tempModule = evt.modules.find(m => m.id === SSO_MODULE_ID) as SsoWebModule;
        const dashboardModule = evt.modules.find((m) => m.id === DashboardModule.MODULE_ID) as DashboardModule | undefined;
        if (!tempModule || !dashboardModule) {
            throw new Error("SSOModule | DashboardModule not found!");
        }
        this.ssoModule = tempModule;
        this.dashboardModule = dashboardModule;
    }

    async beforeEnterAnyRoutes(http: HTTPClient) {
        this.addWidgets();
        if (this.apiCreated) {
            return;
        }
        this.apiCreated = true;

        const useSensorX = await this.useSensorX(http);

        let newHttp = http;
        const path = useSensorX ? 'sensor-x/api' : 'sensor-proxy'
        if(!useSensorX){
            newHttp = new HTTPClient();
            this.ssoModule.addAnauthorizedHandler(newHttp);
            newHttp.addRequestConfigProvider(async config => {
                config.params = config.params || {};
                config.params["SSO_TOKEN"] = await this.ssoModule.getSSOToken();
            })
        }

        const StationApi = (await import("./api/StationApi")).default;
        this.stnApi = new StationApi(newHttp, http, path);

        const SensorApi = (await import("./api/SensorApi")).default;
        this.snsrApi = new SensorApi(newHttp, path);

        const SensorDataApi = (await import("./api/SensorDataApi")).default;
        this.sdApi = new SensorDataApi(newHttp, path);

        const AccountsApi = (await import("./api/AccountsApi")).default;
        this.accntsApi = new AccountsApi(http);

        const NearbyPlotApi = (await import("./api/NearbyPlotApi")).default;
        this.npApi = new NearbyPlotApi(http);

        const SensorConfigApi = (await import("./api/SensorConfigApi")).default;
        this.scApi = new SensorConfigApi(newHttp, path);

        const OAuthApi = (await import("./api/OAuthApi")).default;
        this.AuthApi = new OAuthApi(http);

        const AgrirouterApi = (await import("./api/AgrirouterApi")).default;
        this.agriRoutApi = new AgrirouterApi(http);

        const IotIcons = (await import ("./components/IotIcons")).default;
        this._iotIcons  = new IotIcons();

    }

    public get stationApi() {
        return this.stnApi;
    }

    public get sensorApi() {
        return this.snsrApi;
    }

    public get sensorDataApi() {
        return this.sdApi;
    }

    public get accountsApi() {
        return this.accntsApi;
    }

    public get nearbyPlotsApi() {
        return this.npApi;
    }

    public get sensorConfigApi() {
        return this.scApi;
    }

    public get oAuthApi()
    {
        return this.AuthApi;
    }

    public get agrirouterApi()
    {
        return this.agriRoutApi;
    }

    public async addWidgets() {
        if (this.alreadyAdded) {
            return;
        }
        this.alreadyAdded = true;
        try {
            // const widgetTempList: SensorWidgetParam[] = await this.oldWApi.getWidgetParams();
            // widgetTempList.forEach(element => {
            //     this.widgetParams[element.widgetCode] = element;
            //     let fullTitle = element.widgetCode.toLowerCase();
            //     this.widgetParams[element.widgetCode].title = fullTitle;
            // });
            // createGlobals(this.widgetParams, this.dashboardModule.pinnableTiles, this.dashboardModule.modulesByTileID, this);
            createGlobals(this.widgetParams, this.dashboardModule.pinnableTiles, this.dashboardModule.modulesByTileID, this);
        } catch (error) {
            console.error(error);
        }
    }

    public resetCache() {
        if (this.dashboardModule && this.alreadyAdded) {
            deleteGlobals(this.widgetParams, this.dashboardModule.pinnableTiles);
            this.widgetParams = {};
            this.alreadyAdded = false;
        }
        this.addWidgets()
    }

    private async useSensorX(http: HTTPClient): Promise<boolean>{
        const url = "/anag-api/api/v1/gisparams/SENSORX_SERVER_URL_EXTERNAL";
        const res = await http.get(url);
        return res.data.length > 0;
    }

    public get iotIcons(): IotIcons {
        return this._iotIcons;
    }
}
